import { memo, useMemo } from "react";
import { AlertTriangle, Calendar, Clock, History } from "lucide-react";
import { Link } from "react-router-dom";
import type { ScheduleSource } from "@/data/types";
import { MODE_CATALOG, MODE_TONE } from "@/data/modeCatalog";
import { cn } from "@/lib/cn";
import { formatDateTime, formatRelativeFuture } from "@/lib/format";
import { useT } from "@/lib/i18n";
import { HoldToConfirm } from "@/components/ui/HoldToConfirm";
import { lastRunHistoricalHref, occurrenceWindow } from "./EntryEditor";

/**
 * Three-tile compact summary: what's running now, what's coming next, and
 * the override panel trigger. The override badge here only opens the
 * OverridePanel — the actual apply / hold-to-confirm happens there.
 */
export const ActiveStatePanel = memo(function ActiveStatePanel({
  source,
  onOpenOverride,
  onClearOverride,
}: {
  source: ScheduleSource | null;
  onOpenOverride: () => void;
  onClearOverride: () => Promise<void> | void;
}) {
  const t = useT();
  const active = source?.active_now;
  const mode = active?.mode ?? "gridZeroControl";
  const entry = active?.entry;
  const override = source?.override;
  // Most recent completed occurrence of the active entry — evidence link
  // to the Historical analyzer (LINK-06 / MKT-10). Falls back to the
  // browser timezone inside projectInTz when the doc carries no tz.
  const tz = source?.doc?.timezone ?? "";
  const lastRun = useMemo(
    () => (entry ? occurrenceWindow(entry, tz) : null),
    [entry, tz],
  );
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
      {/* Active mode */}
      <section className={cn(
        "card p-4",
        override && "border-warn/40",
      )}>
        <div className="text-[10px] uppercase tracking-wider text-text-faint font-semibold flex items-center gap-1">
          <Calendar className="h-3 w-3" />
          {t("manage.active.activeMode")}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <span className={cn("inline-flex items-center rounded-md border px-2.5 py-1 text-sm font-semibold", MODE_TONE[mode])}>
            {MODE_CATALOG[mode].label}
          </span>
          <div className="text-[11px] text-text-muted">
            {t("manage.active.source")}{" "}
            <strong className="text-text">
              {source?.source ?? "—"}
            </strong>
            {entry && (
              <>
                {` · ${t("manage.active.entry")} `}
                <code className="text-text">{entry.id}</code>
                {entry.name && <span className="text-text-faint"> · {entry.name}</span>}
              </>
            )}
          </div>
        </div>
        <div className="mt-2 max-w-xl text-[11px] text-text-muted">
          {MODE_CATALOG[mode].summary}
        </div>
        {lastRun && (
          <div className="mt-2">
            <Link
              to={lastRunHistoricalHref(lastRun)}
              title={t("manage.active.lastRunTitle", { start: formatDateTime(lastRun.start), end: formatDateTime(lastRun.end) })}
              className="inline-flex h-7 items-center gap-1 rounded-md border border-edge bg-surface px-2 text-[11px] text-text-muted hover:bg-surface-2 hover:text-text"
            >
              <History className="h-3 w-3" /> {t("manage.active.viewLastRun")}
            </Link>
          </div>
        )}
      </section>

      {/* Next change */}
      <section className="card p-4">
        <div className="text-[10px] uppercase tracking-wider text-text-faint font-semibold flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {t("manage.active.nextChange")}
        </div>
        {source?.next_change ? (
          <div className="mt-1">
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="font-mono text-sm text-text">
                {formatDateTime(source.next_change.at)}
              </span>
              <span className="text-[11px] text-text-muted">
                ({formatRelativeFuture(source.next_change.at - Date.now())})
              </span>
            </div>
            <div className="mt-1 text-[11px] text-text-muted">
              → <span className={cn("inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-semibold", MODE_TONE[source.next_change.mode])}>
                {MODE_CATALOG[source.next_change.mode]?.label ?? source.next_change.mode}
              </span>
            </div>
          </div>
        ) : (
          <div className="mt-2 text-[11px] text-text-faint">
            {t("manage.active.noTransition")}
          </div>
        )}
      </section>

      {/* Override controls */}
      <section className={cn(
        "card p-4",
        override && "border-warn/40 bg-warn/[0.04]",
      )}>
        <div className="text-[10px] uppercase tracking-wider text-text-faint font-semibold flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" />
          {t("manage.active.manualOverride")}
        </div>
        {override ? (
          <div className="mt-1 space-y-1">
            <div className="flex flex-wrap items-baseline gap-2">
              <span className={cn("inline-flex items-center rounded border px-2 py-0.5 text-xs font-semibold", MODE_TONE[override.mode])}>
                {MODE_CATALOG[override.mode].label}
              </span>
              <span className="text-[11px] text-text-muted">{t("manage.active.until", { time: formatDateTime(override.expires_at) })}</span>
            </div>
            {override.reason && (
              <div className="text-[11px] text-text-faint italic">"{override.reason}"</div>
            )}
            <div className="flex gap-2 pt-1">
              <button
                onClick={onOpenOverride}
                className="inline-flex h-7 items-center rounded-md border border-warn/40 bg-warn/10 px-2 text-[11px] text-warn hover:bg-warn/20"
              >
                {t("manage.active.replace")}
              </button>
              <HoldToConfirm
                label={t("manage.active.clear")}
                intent="warn"
                className="h-7 px-2 text-[11px] normal-case tracking-normal"
                onConfirm={() => void onClearOverride()}
              />
            </div>
          </div>
        ) : (
          <div className="mt-1">
            <p className="text-[11px] text-text-muted">
              {t("manage.active.overrideExplain")}
            </p>
            <div className="mt-2">
              <button
                onClick={onOpenOverride}
                className="inline-flex h-8 items-center gap-1.5 rounded-md border border-warn/40 bg-warn/10 px-3 text-xs font-medium text-warn hover:bg-warn/20"
              >
                <AlertTriangle className="h-3.5 w-3.5" /> {t("manage.active.applyOverride")}
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
});
